import React, { useState, useEffect, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Tooltip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import { FixedSizeList as List } from 'react-window';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';

const SpareIndex = ({ hasCreateMasterPermission, hasEditMasterPermission }) => {
    const history = useHistory();
    const [data, setData] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const { isAuthenticated, companyId, permissions, token, logout, roleName } = useAuth();
    const [deleteItemId, setDeleteItemId] = useState(null);

    useEffect(() => {
        // Check if the token is expired
        if (isAuthenticated && tokenIsExpired(token)) {
            // Token is expired, log out the user
            logout();
            return;
        }

        // Token is not expired, fetch data
        fetchTableData();
    }, [isAuthenticated, companyId, token, logout, roleName]);

    const fetchTableData = async () => {
        try {
            const response = await fetchData('SparePartsApi', `/${companyId}`, token);
            console.log('API Response:', response);
            if (response.responseCode === '1111') {
                setData(response.response_data);
                setHasMore(response.response_data.length > 0);
            } else {
                console.error('Unexpected response code:', response.responseCode);
            }
        } catch (error) {
            console.error('Error fetching Item Details:', error);
            if (error.response && error.response.status === 401) {
                logout();
                history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            }
        }
    };

    const handleDelete = async () => {
        try {
            await deleteData('SparePartsApi', `${deleteItemId}`);
            fetchTableData();
            setDeleteItemId(null);
            setConfirmDialogOpen(false);
        } catch (error) {
            console.error('Error deleting record:', error);
        }
    };

    const handleActivate = async (itemId) => {
        try {
            await activateData('SparePartsApi', `${itemId}`);
            fetchTableData();
        } catch (error) {
            console.error('Error activating record:', error);
        }
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const Row = useCallback(({ index, style }) => {
        const item = filteredData[index];
        const calculatedValue = (item.saleRate * item.gstRate) / 100;
        const totaltaxrate = item.saleRate + calculatedValue;

        return (
            <TableRow key={item.id} style={style}>
                {/* <TableCell sx={{ borderBottom: '1px solid #000000', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'center', fontSize: '13px' }}>{index + 1}</TableCell>
                <TableCell sx={{ borderBottom: '1px solid #000000', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'center', fontSize: '13px'}}> &nbsp{item.hsnCode}</TableCell>
                <TableCell sx={{ borderBottom: '1px solid #000000', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'center', fontSize: '13px' }}>{item.code}</TableCell> */}
                <TableCell sx={{ borderBottom: '1px solid #000000', color: 'black', fontWeight: 'bolder', textAlign: 'center', fontSize: '13px' }}>
                    {index + 1}
                </TableCell>
                <TableCell sx={{ borderBottom: '1px solid #000000', color: 'black', fontWeight: 'bolder', padding: '5px 5px 5px 50px', textAlign: 'center', fontSize: '13px' }}>
                    {item.code}
                </TableCell>



                <TableCell sx={{ borderBottom: '1px solid #000000', color: 'black', fontWeight: 'bolder', padding: '6px 6px 6px 60px', textAlign: 'left', fontSize: '13px', width: '550px' }}>{item.name}</TableCell>
                {/* {roleName !== "Engineer" && (
                    <TableCell sx={{ borderBottom: '1px solid #000000', color: 'black', fontWeight: 'bolder', padding: '5px 5px 5px 50px', textAlign: 'center', fontSize: '13px' }}>{item.purchaseRate}</TableCell>
                )} */}
                <TableCell sx={{ borderBottom: '1px solid #000000', color: 'black', fontWeight: 'bolder', padding: '5px 5px 5px 50px', textAlign: 'center', fontSize: '13px' }}>
                    {item.hsnCode}
                </TableCell>

                <TableCell sx={{ borderBottom: '1px solid #000000', color: 'black', fontWeight: 'bolder', padding: '5px 5px 5px 50px', textAlign: 'center', fontSize: '13px' }}>{item.gstRate}</TableCell>
                <TableCell sx={{ borderBottom: '1px solid #000000', color: 'black', fontWeight: 'bolder', padding: '6px 6px 6px 60px', textAlign: 'center', fontSize: '13px', width: '150px' }}>{item.saleRate.toFixed(2)}</TableCell>
                <TableCell sx={{ borderBottom: '1px solid #000000', color: 'black', fontWeight: 'bolder', padding: '6px 6px 6px 60px', textAlign: 'center', fontSize: '13px', width: '150px', color: 'darkred', fontWeight: 'bolder' }}>{totaltaxrate.toFixed(2)}</TableCell>
                <TableCell sx={{ borderBottom: '1px solid #000000', color: 'black', fontWeight: 'bolder', padding: '6px 6px 6px 60px', textAlign: 'right', fontSize: '13px' }}>{item.minimumStock}</TableCell>
                <TableCell sx={{ borderBottom: '1px solid #000000', color: 'black', fontWeight: 'bolder', padding: '6px 6px 6px 60px', textAlign: 'right', fontSize: '13px' }}>{item.totalStock}</TableCell>
                {roleName !== "Engineer" && (
                    <TableCell sx={{ borderBottom: '1px solid #000000', padding: '6px 6px 6px 60px', textAlign: 'right', fontSize: '12px' }}>
                        <Tooltip title="Edit" arrow>
                            <IconButton component={Link} to={`/SpareEdit/${item.id}`} sx={{ padding: '0.5px 0.5px  0.5px 5px' }}>
                                <EditIcon style={{ color: 'darkblue' }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete" arrow>
                            <IconButton onClick={() => openConfirmDialog(item.id)} sx={{ padding: '0.5px 0.5px  0.5px 5px' }}>
                                <DeleteIcon style={{ color: 'red' }} />
                            </IconButton>
                        </Tooltip>

                    </TableCell>
                )}
            </TableRow>
        );
    }, [filteredData, roleName]);

    return (
        <div className="w-full mx-auto" style={{ width: '98%', marginTop: '15px' }}>
            <div className='w-full mb-2 flex'>
                <h6 className='productHead flex-grow linkcolor' style={{ color: 'black', fontFamily: 'Georgia, serif', fontWeight: 'bolder' }}>SPARE PARTS</h6>
                <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
                    <SearchIcon onClick={toggleSearchInput} style={{ cursor: 'pointer' }} />
                    {showSearchInput && (
                        <input
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={handleSearch}
                            style={{ marginLeft: '-30px', width: '200px' }}
                        />
                    )}

                    {roleName !== "Engineer" && (
                        <Button
                            component={Link}
                            to="/SpareCreate"
                            className="linkcolor"
                            style={{ marginLeft: '8px', fontWeight: 'bolder' }}
                        >
                            <AddIcon /> CREATE
                        </Button>
                    )}
                </div>
            </div>
            <div className='w-full mb-5 flex' style={{ maxHeight: '490px', overflowY: 'auto', marginTop: '-15px' }}>
                <TableContainer component={Paper}>
                    <Table stickyHeader>
                        <TableHead sx={{ backgroundColor: '#090040' }}>
                            <TableRow>
                                <TableCell sx={{ backgroundColor: '#090040', fontWeight: 'bolder', fontSize: '12px', padding: '5px', textAlign: 'left', color: 'white', textTransform: 'uppercase' }}>
                                    <span style={{ marginLeft: '-5px' }}>Sl.No</span>
                                    <span style={{ marginLeft: '50px', color: 'white', fontWeight: 'bolder' }}>Code</span>
                                    <span style={{ marginLeft: '95px', color: 'white', fontWeight: 'bolder' }}>Name</span>
                                    <span style={{ marginLeft: '490px', color: 'white', fontWeight: 'bolder' }}>HSN</span>
                                    <span style={{ marginLeft: '50px', color: 'white', fontWeight: 'bolder' }}>GST</span>
                                    <span style={{ marginLeft: '75px', color: 'white', fontWeight: 'bolder' }}>Rate</span>
                                    <span style={{ marginLeft: '70px', color: 'white', fontWeight: 'bolder' }}>MRP(Incl.Tax)</span>
                                    <span style={{ marginLeft: '20px', color: 'white', fontWeight: 'bolder' }}>M.Stock</span>
                                    <span style={{ marginLeft: '50px', color: 'white', fontWeight: 'bolder' }}>T.Stock</span>
                                    <span style={{ marginLeft: '55px', color: 'white', fontWeight: 'bolder' }}>Action</span>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <List
                                height={500}
                                itemCount={filteredData.length}
                                itemSize={60}
                                width="100%"
                            >
                                {Row}
                            </List>
                        </TableBody>
                    </Table>
                </TableContainer>
                <Dialog
                    open={confirmDialogOpen}
                    onClose={closeConfirmDialog}
                >
                    <DialogTitle>Confirm Deletion</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to delete this item?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeConfirmDialog} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={handleDelete} color="secondary">
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        </div >
    );
};

export default SpareIndex;



